<template>
  <v-form @submit.prevent="preSubmit()">
  <v-card class="blue-grey lighten-5">
    <form-header
      :fullscreen="false"
      :form="form"
      tour="rate-calendar-creation-form"
    >
      <template #title>
        {{$lang("rate-calendar.title_create")}}
      </template>
      <template #right-buttons v-if="initialized">
        <span class="caption mr-2">
          {{roomType.unit_category.name}} /
          {{roomType.rate_plan.name}} /
          <v-icon small>mdi-human-male</v-icon>{{roomType.guests}}
        </span>
        <v-chip color="white" label class="overline warning--text text-right" v-if="!!derivationRule.parent">
          <v-btn icon @click="isOverrideDerivation = !isOverrideDerivation">
            <v-icon v-if="!isOverrideDerivation" color="warning">mdi-link</v-icon>
            <v-icon v-if="isOverrideDerivation" color="primary">mdi-link-off</v-icon>
          </v-btn>
          {{ $lang('derivations.fields.derived_from') }} {{ derivationRule.parent.name }}
        </v-chip>
      </template>
    </form-header>
    <v-card-text>
      <v-container v-if="!initialized" bg fill-height text-xs-center>
        <v-row justify="center">
          <div class="text-center">
            <v-progress-circular
              :size="70"
              :width="7"
              color="primary"
              indeterminate
            />
          </div>
        </v-row>
      </v-container>
      <v-container v-if="initialized">
        <v-row>
          <v-col md="12" xs="12">
            <v-card outlined>
              <v-card-title class="text-uppercase">
                {{ $lang("rate-calendar.period") }}
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="3" xs="12">
                    <v-date
                      v-model="form.start_date"
                      :label="$lang('rate-calendar.fields.from')"
                      :error="!!validation.start_date"
                      :error-messages="!!validation.start_date ? validation.start_date : []"
                      @change="resetForm"
                      :min="$moment().format('YYYY-MM-DD')"
                    />
                  </v-col>
                  <v-col cols="12"  md="3" xs="12">
                    <v-date
                      v-model="form.end_date"
                      :min="form.start_date"
                      :label="$lang('rate-calendar.fields.to')"
                      :error="!!validation.end_date"
                      :error-messages="!!validation.end_date ? validation.end_date : []"
                      @change="resetForm"
                    />
                  </v-col>
                  <v-col cols="12" md="6" xs="12" align="center" align-self="center">
                    <v-btn-toggle v-if="!$isMobile()" multiple v-model="form.weekdays" background-color="white" color="teal">
                      <v-btn :value="1" small>
                        {{ $lang('main.weekdays_short.mon') }}
                      </v-btn>
                      <v-btn :value="2" small>
                        {{ $lang('main.weekdays_short.tue') }}
                      </v-btn>
                      <v-btn :value="3" small>
                        {{ $lang('main.weekdays_short.wed') }}
                      </v-btn>
                      <v-btn :value="4" small>
                        {{ $lang('main.weekdays_short.thu') }}
                      </v-btn>
                      <v-btn :value="5" small>
                        {{ $lang('main.weekdays_short.fri') }}
                      </v-btn>
                      <v-btn :value="6" small>
                        {{ $lang('main.weekdays_short.sat') }}
                      </v-btn>
                      <v-btn :value="0" small>
                        {{ $lang('main.weekdays_short.sun') }}
                      </v-btn>
                    </v-btn-toggle>
                    <v-row v-if="$isMobile()">
                      <v-col cols="6">
                        <v-checkbox :value="1" :label="$lang('main.weekdays.mon')" dense hide-details />
                        <v-checkbox :value="2" :label="$lang('main.weekdays.tue')" dense hide-details />
                        <v-checkbox :value="3" :label="$lang('main.weekdays.wed')" dense hide-details />
                        <v-checkbox :value="4" :label="$lang('main.weekdays.thu')" dense hide-details />
                      </v-col>
                      <v-col cols="6">
                        <v-checkbox :value="5" :label="$lang('main.weekdays.fri')" dense hide-details />
                        <v-checkbox :value="6" :label="$lang('main.weekdays.sat')" dense hide-details />
                        <v-checkbox :value="0" :label="$lang('main.weekdays.sun')" dense hide-details />
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6" xs="12">
            <v-card outlined>
              <v-card-title class="text-uppercase">
                {{ $lang("rate-calendar.values") }}
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <v-row>
                  <v-col xs="6" md="auto" align="center">
                    <v-btn-toggle v-model="form.auto_availability" mandatory background-color="white" color="teal">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            :value="$const('ConstBoolean', 'TRUE')"
                            small
                            v-on="on"
                          >
                            <v-icon small color="red">mdi-auto-fix</v-icon> AUTO
                          </v-btn>
                          <v-btn
                            :value="$const('ConstBoolean', 'FALSE')"
                            small
                          >
                            <v-icon small>mdi-equal</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $lang('rate-calendar.fields.auto_availability') }}</span>
                      </v-tooltip>
                    </v-btn-toggle>
                  </v-col>
                  <v-col>
                    <v-text-field
                      v-model="form.availability"
                      dense
                      :label="$lang('rate-calendar.fields.availability')"
                      :hint="$lang('rate-calendar.hints.availability')"
                      @input="checkMaxAvailability()"
                      @click="availabilityWarning()"
                      @change="availabilityChanged()"
                      :disabled="!!form.auto_availability || (!!derivationRule.availability && !isOverrideDerivation)"
                      :error="!!validation.availability"
                      :error-messages="!!validation.availability ? validation.availability : []"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col md="auto" xs="12" align="center">
                    <v-btn-toggle v-model="form.price_variation" mandatory background-color="white" color="teal">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            :value="1"
                            small
                            style="min-width: initial;"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>mdi-equal</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $lang('rate-calendar.variation.exact') }}</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            :value="2"
                            small
                            style="min-width: initial;"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>mdi-arrow-up-down</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $lang('rate-calendar.variation.value') }}</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            :value="3"
                            small
                            style="min-width: initial;"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>mdi-percent</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $lang('rate-calendar.variation.percent') }}</span>
                      </v-tooltip>
                    </v-btn-toggle>
                  </v-col>
                  <v-col>
                    <v-text-field
                      v-model="form.amount"
                      dense
                      :label="$lang('rate-calendar.fields.price')"
                      :error="!!validation.amount"
                      :error-messages="!!validation.amount ? validation.amount : []"
                      @change="checkMinPrice"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col md="6" xs="12">
            <v-card outlined>
              <v-card-title class="text-uppercase">
                {{ $lang("rate-calendar.length_of_stay") }}
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <v-row align="center">
                  <v-col cols="12">
                    <v-text-field
                      v-model="form.min_stay"
                      dense
                      :label="$lang('rate-calendar.fields.min')"
                      :hint="$lang('rate-calendar.hints.min')"
                      :prepend-icon="!!derivationRule.length_of_stay ? 'mdi-link' : null"
                      :disabled="!!derivationRule.length_of_stay && !isOverrideDerivation"
                      :error="!!validation.min_stay"
                      :error-messages="!!validation.min_stay ? validation.min_stay : []"
                      :suffix="form.min_stay > 1 ? $lang('main.words.days') : $lang('main.words.day')"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="form.max_stay"
                      dense
                      :label="$lang('rate-calendar.fields.max')"
                      :hint="$lang('rate-calendar.hints.max')"
                      :prepend-icon="!!derivationRule.length_of_stay ? 'mdi-link' : null"
                      :disabled="!!derivationRule.length_of_stay && !isOverrideDerivation"
                      :error="!!validation.max_stay"
                      :error-messages="!!validation.max_stay ? validation.max_stay : []"
                      :suffix="$lang('main.words.days')"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col md="12" xs="12">
            <v-card outlined>
              <v-card-text>
                <v-expansion-panels flat>
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <span>
                        <v-icon color="blue" class="mr-1">mdi-cog</v-icon>{{ $lang('rate-calendar.advanced_settings') }}
                      </span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-row>
                          <v-col cols="12" md="4" xs="12">
                            <v-text-field v-model="form.cut_off"
                                          :label="$lang('rate-calendar.fields.cutoff')"
                                          :hint="$lang('rate-calendar.hints.cutoff')"
                                          :prepend-icon="!!derivationRule.length_of_stay ? 'mdi-link' : 'mdi-content-cut'"
                                          :disabled="!!derivationRule.restrictions  && !isOverrideDerivation"
                                          :error="!!validation.cut_off"
                                          :error-messages="!!validation.cut_off ? validation.cut_off : []"
                            >
                            </v-text-field>
                          </v-col>
                          <v-col cols="12" md="4" xs="12">
                            <v-boolean v-model="form.skip_derived"
                                      :label="$lang('rate-calendar.fields.skip_derived_tariff')">
                            </v-boolean>
                          </v-col>
                          <v-col cols="12" md="4" xs="12">
                            <v-switch v-model="isOverrideDerivation"
                                      :label="$lang('rate-calendar.fields.override_derivations')">
                            </v-switch>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12" xs="12" md="4">
                            <v-icon small color=red>mdi-cancel</v-icon> {{ $lang('rate-calendar.fields.stop_sell') }}
                            <v-radio-group
                              v-model="form.stop_sell"
                              row
                              :disabled="!!derivationRule.stop_sell  && !isOverrideDerivation"
                              :prepend-icon="!!derivationRule.stop_sell ? 'mdi-link' : null"
                            >
                              <v-radio :label="$lang('main.words.yes')" :value="1" color="red"></v-radio>
                              <v-radio :label="$lang('main.words.no')" :value="0" color="primary"></v-radio>
                              <v-radio :label="$lang('rate-calendar.no_change')" :value="false" color="success"></v-radio>
                            </v-radio-group>
                          </v-col>
                          <v-col cols="12" xs="12" md="4">
                            <v-icon small color=primary>mdi-login</v-icon> {{ $lang('rate-calendar.fields.cta') }}
                            <v-radio-group
                              v-model="form.cta"
                              row
                              :disabled="!!derivationRule.restrictions && !isOverrideDerivation"
                              :prepend-icon="!!derivationRule.restrictions ? 'mdi-link' : null"
                            >
                              <v-radio :label="$lang('main.words.yes')" :value="1" color="red"></v-radio>
                              <v-radio :label="$lang('main.words.no')" :value="0" color="primary"></v-radio>
                              <v-radio :label="$lang('rate-calendar.no_change')" :value="false" color="success"></v-radio>
                            </v-radio-group>
                          </v-col>
                          <v-col cols="12" xs="12" md="4">
                            <v-icon small color=green>mdi-logout</v-icon> {{ $lang('rate-calendar.fields.ctd') }}
                            <v-radio-group
                              v-model="form.ctd"
                              row
                              :disabled="!!derivationRule.restrictions && !isOverrideDerivation"
                              :prepend-icon="!!derivationRule.restrictions ? 'mdi-link' : null"
                            >
                              <v-radio :label="$lang('main.words.yes')" :value="1" color="red"></v-radio>
                              <v-radio :label="$lang('main.words.no')" :value="0" color="primary"></v-radio>
                              <v-radio :label="$lang('rate-calendar.no_change')" :value="false" color="success"></v-radio>
                            </v-radio-group>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12" xs="12" md="4">
                            <v-boolean
                              v-model="form.auto_availability"
                              :label="$lang('rate-calendar.fields.auto_availability')"
                              :hint="$lang('rate-calendar.hints.auto_availability')"
                              persistent-hint
                            />
                          </v-col>
                        </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
    <v-card-actions>
      <v-container>
      <v-row>
        <v-col cols="12" xs="12" md="6">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn color="primary" class="mr-2" @click="closeDates()" v-on="on" :loading="loading && !!form.stop_sell" :disabled="loading || (!!derivationRule.stop_sell  && !isOverrideDerivation)">
                {{ $lang("rate-calendar.close_dates") }}
              </v-btn>
            </template>
            <span>{{ $lang("rate-calendar.hints.close_dates") }}</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn color="success" class="mr-2" @click="openDates()" v-on="on" :loading="loading && !form.stop_sell" :disabled="loading || (!!derivationRule.stop_sell  && !isOverrideDerivation)">
                {{ $lang("rate-calendar.open_dates") }}
              </v-btn>
            </template>
            <span>{{ $lang("rate-calendar.hints.open_dates") }}</span>
          </v-tooltip>
        </v-col>
        <v-col cols="12" xs="12" md="6" align="end">
          <v-btn color="success" type="submit" :loading="loading" :disabled="loading" class="mr-2">
            {{ $lang("main.crud.save") }}
          </v-btn>
          <v-btn color="error" @click="cancel()">
            {{ $lang("main.crud.cancel") }}
          </v-btn>
        </v-col>
      </v-row>
      </v-container>
    </v-card-actions>
  </v-card>
  <v-confirmation
    v-model="isOpenSubmitConfirm"
    :message="$lang('rate-calendar.hints.manual_availability')"
    :options="{
      loading: loading,
      yes: () => submit()
    }"
  />
  </v-form>
</template>

<script>
import { mapState } from "vuex";

import {
  CREATE_RATE,
  CREATE_RATE_RESET,
  GET_RATE_CALENDAR_COLLECTION
} from "@/store/rate-calendar/actions";

import {
  CREATE_CALENDAR_RATE,
  CREATE_CALENDAR_RATE_RESET,
} from "@/store/calendar/actions";

import {
  GET_ROOM_TYPES_SINGLE
} from "@/store/room-types/actions";

import formable from "@/mixins/formable";
import dataFilter from "./mixins/data-filter";
import MixpanelEvents from "@/constants/events";

export default {
  name: "CreationForm",
  mixins: [
    formable,
    dataFilter
  ],
  props: [
    'data',
    'version'
  ],
  data() {
    return {
      form: {
        end_date: null,
        start_date: null,
        min_stay: null,
        max_stay: null,
        weekdays: [0,1,2,3,4,5,6],
        skip_derived: 0,
        price_variation: 1,
        cta: false,
        ctd: false,
        stop_sell: false,
        auto_availability: 0,
        availability: null,
        cut_off: null,
      },
      availabilityDisabled: true,
      isOverrideDerivation: false,
      isAvailabilityManuallyChanged: false,
      isOpenSubmitConfirm: false,
      trackActionName: null,
    }
  },
  beforeMount() {
    if (this.data.start_date === this.data.end_date) {
      this.$store.dispatch(GET_RATE_CALENDAR_COLLECTION, {
        room_type_id: this.data.room_type_id,
        date: this.data.start_date,
      }).then(response => {
        response.collection.map(item => {
          this.form = {
            ...this.form,
            ...item,
          };

          delete this.form['id'];
          delete this.form['isRefundable'];
          delete this.form['date'];
          delete this.form['created_at'];
          delete this.form['updated_at'];
        });
      });
    }

    this.form.end_date = this.data.end_date;
    this.form.start_date = this.data.start_date;
    this.form.room_type_id = this.data.room_type_id;

    this.getRoomType();
  },
  methods: {
    resetAction() {
      return this.version === 1 ? CREATE_RATE_RESET : CREATE_CALENDAR_RATE_RESET;
    },
    submitAction() {
      return this.version === 1 ? CREATE_RATE : CREATE_CALENDAR_RATE;
    },
    submitActionOptions() {
      if (this.form.cta === false) {
        delete this.form['cta'];
      }
      if (this.form.ctd === false) {
        delete this.form['ctd'];
      }
      if (this.form.stop_sell === false) {
        delete this.form['stop_sell'];
      }

      return this.form;
    },
    track() {
      const amountVariation = {
        1: 'exact',
        2: 'increment',
        3: 'percent',
      }
      this.$monitorService.track(MixpanelEvents.SINGLE_RATE_UPDATED, {
        is_availability_custom: this.isAvailabilityManuallyChanged,
        action: this.trackActionName ? this.trackActionName : 'saved',
        amount_variation: this.form.price_variation ? amountVariation[this.form.price_variation]: null,
        used_advanced_features: this.form.cta || this.form.ctd || this.form.cut_off > 0,
      });
    },
    closeDates() {
      this.form = {
        ...this.form,
        stop_sell: 1,
        skip_derived: 0,
        price_variation: 1,
        end_date: this.form.end_date,
        start_date: this.form.start_date,
        room_type_id: this.form.room_type_id,
        amount: null,
      };

      this.trackActionName = 'close_dates';

      this.submit();
    },
    openDates() {
      this.form = {
        ...this.form,
        stop_sell: 0,
        skip_derived: 0,
        price_variation: 1,
        auto_availability: 1,
        end_date: this.form.end_date,
        start_date: this.form.start_date,
        room_type_id: this.form.room_type_id,
        availability: null,
        amount: null,
      };

      this.trackActionName = 'open_dates';

      this.submit();
    },
    getRoomType() {
      this.$store.dispatch(GET_ROOM_TYPES_SINGLE, {
        id: this.data.room_type_id,
        params: {
          _data: {
            relations: {
              unit_category: {
                selects: [
                  'id',
                  'name'
                ]
              },
              rate_plan: {
                selects: [
                  'id',
                  'name'
                ]
              },
              derivation_rule: {
                select: ['*'],
                relations: {
                  parent: {
                    selects: [
                      'id',
                      'name',
                    ]
                  }
                }
              }
            }
          }
        }
      });
    },
    checkMinPrice() {
      const minPrice = parseFloat(this.roomType.min_price);
      const amount = parseFloat(this.form.amount);

      if (amount < minPrice && this.form.price_variation === 1) {
        this.showNotification(this.$lang('rate-calendar.validation.min_price', {
          price: this.roomType.min_price
        }), 'warning');
      }
    },
    checkMaxAvailability() {
      const maxAvailability = parseFloat(this.roomType.quantity);
      const availability = parseFloat(this.form.availability);

      if (availability > maxAvailability) {
        this.showNotification(this.$lang('rate-calendar.validation.availability'), 'warning');
      }
    },
    availabilityWarning() {
      this.showNotification(this.$lang('rate-calendar.availability_warning'), 'warning');
    },
    availabilityChanged() {
      this.isAvailabilityManuallyChanged = true;
    },
    preSubmit() {
      if (this.isAvailabilityManuallyChanged) {
        this.isOpenSubmitConfirm = true;
        return;
      }

      this.submit();
    },
    resetForm() {
      this.form = {
        ...this.form,
        weekdays: [0,1,2,3,4,5,6],
        skip_derived: 0,
        price_variation: 1,
        cta: false,
        ctd: false,
        stop_sell: false,
        amount: null,
        availability: null,
        auto_availability: 0,
        min_stay: null,
        max_stay: null,
      };
    },
  },
  computed: {
    initialized() {
      return !this.roomTypeRequest.loading;
    },
    loading() {
      return this.version === 1 ? this.loading1 : this.loading2;
    },
    validation() {
      return this.version === 1 ? this.validation1 : this.validation2;
    },
    ...mapState({
      loading2: state => state.calendar.creationRequest.loading,
      validation2: state => state.calendar.creationRequest.validation,
      loading1: state => state.rateCalendar.creationRequest.loading,
      validation1: state => state.rateCalendar.creationRequest.validation,
      roomTypeRequest: state => state.roomTypes.getSingleRequest,
    }),
    roomType() {
      return this.roomTypeRequest.item;
    },
    derivationRule() {
      if (!this.roomTypeRequest.item.derivation_rule) {
        return {};
      }

      return this.roomTypeRequest.item.derivation_rule;
    }
  },
  watch: {
    'form.auto_availability'(val) {
      if (val === 1) {
        this.form.availability = null;
      }
    }
  }
};
</script>
